import { CheckCircle, Clock, Play, XCircle } from 'lucide-react-native';
import { StyleSheet } from 'react-native';
import { Badge, Box, HStack, Text, VStack } from '../../../components/primitives';
import { Button } from '../../../components/primitives/Button';
import { useTranslation } from '../../../contexts/TranslationContext';
import { colors } from '../../../theme/colors';
import { tokens } from '../../../theme/tokens';
import { darkPrecisionFontFamilies } from '../../../theme/typography';
import { type AgentToolAction, ToolActionStatus } from '../types';

interface ToolActionApprovalCardProps {
  action: AgentToolAction;
  onApprove?: (actionId: string) => void;
  onReject?: (actionId: string) => void;
  isApproving?: boolean;
  isRejecting?: boolean;
}

const TOOL_LABELS: Record<string, string> = {
  create_lead: 'Create Lead',
  create_leads_from_research: 'Create Leads From Research',
  move_lead_stage: 'Move Lead Stage',
  add_note: 'Add Note',
  assign_lead: 'Assign Lead',
  update_lead_tags: 'Update Tags',
  update_lead_custom_fields: 'Update Fields',
  get_lead_details: 'Get Lead Details',
  list_funnel_stages: 'List Stages',
  search_leads: 'Search Leads',
  list_team_members: 'List Team',
};

const STATUS_COLOR_TO_ICON_COLOR: Record<string, string> = {
  amber: colors.status.warning.main,
  green: colors.status.success.main,
  red: colors.status.error.main,
};

const STATUS_CONFIG: Record<
  string,
  { color: 'amber' | 'green' | 'red' | 'cyan' | 'neutral'; icon: typeof Clock }
> = {
  pending: { color: 'amber', icon: Clock },
  approved: { color: 'cyan', icon: Play },
  executing: { color: 'cyan', icon: Play },
  completed: { color: 'green', icon: CheckCircle },
  rejected: { color: 'red', icon: XCircle },
  failed: { color: 'red', icon: XCircle },
};

function truncateValue(value: unknown): string {
  if (typeof value === 'string') {
    return value.length > 40 ? `${value.slice(0, 40)}...` : value;
  }
  return JSON.stringify(value);
}

function formatParams(params: Record<string, unknown>): string {
  const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== null);
  if (entries.length === 0) return '';
  return entries.map(([key, value]) => `${key}: ${truncateValue(value)}`).join(', ');
}

export function ToolActionApprovalCard({
  action,
  onApprove,
  onReject,
  isApproving,
  isRejecting,
}: Readonly<ToolActionApprovalCardProps>) {
  const { t } = useTranslation();
  const config = STATUS_CONFIG[action.status] ?? STATUS_CONFIG.pending;
  const StatusIcon = config.icon;
  const isPending = action.status === ToolActionStatus.PENDING;
  const toolLabel = TOOL_LABELS[action.toolName] ?? action.toolName.replaceAll('_', ' ');
  const paramsDisplay = formatParams(action.parameters);

  return (
    <Box borderRadius="md" padding="sm" backgroundColor={colors.surface.row} style={styles.card}>
      <VStack spacing="xs">
        <HStack spacing="sm" align="center" justify="between">
          <HStack spacing="xs" align="center">
            <StatusIcon
              size={14}
              color={STATUS_COLOR_TO_ICON_COLOR[config.color] ?? colors.primary[400]}
              strokeWidth={2}
            />
            <Text variant="caption" color={colors.text.primary} style={styles.toolName}>
              {toolLabel}
            </Text>
          </HStack>
          <Badge color={config.color} variant="subtle" size="sm">
            {action.status}
          </Badge>
        </HStack>

        {Boolean(paramsDisplay) && (
          <Text
            variant="caption"
            color={colors.text.tertiary}
            numberOfLines={2}
            style={styles.params}
          >
            {paramsDisplay}
          </Text>
        )}

        {action.errorMessage && (
          <Text variant="caption" color={colors.status.error.light} numberOfLines={2}>
            {action.errorMessage}
          </Text>
        )}

        {isPending && (onApprove || onReject) && (
          <HStack spacing="xs" justify="end" style={styles.actions}>
            {onReject && (
              <Button
                variant="ghost"
                size="sm"
                onPress={() => onReject(action.id)}
                disabled={isRejecting}
                textColor={colors.status.error.main}
              >
                {t('agents.toolActions.reject')}
              </Button>
            )}
            {onApprove && (
              <Button
                variant="primary"
                size="sm"
                onPress={() => onApprove(action.id)}
                disabled={isApproving}
                loading={isApproving}
              >
                {t('agents.toolActions.approve')}
              </Button>
            )}
          </HStack>
        )}
      </VStack>
    </Box>
  );
}

const styles = StyleSheet.create({
  card: {
    borderWidth: 1,
    borderColor: colors.border.subtle,
  },
  toolName: {
    fontWeight: '600',
  },
  params: {
    fontSize: tokens.fontSize.xs,
    lineHeight: 15,
    fontFamily: darkPrecisionFontFamilies.metric,
  },
  actions: {
    marginTop: tokens.spacing.xxs,
  },
});
